import React, { useEffect, useMemo, useState } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import Heading from '../../generic-components/Heading';
import { ReactComponent as CloseIcon } from '../../assets/icons/svg/shared/ic-shared-close.svg';

interface ILiliMultiSelectProps {
  maxSelectedLabels: number;
  items: { title: string; value: string }[];
  onChange: (selectedValues: string[]) => void;
  defaultValue?: string[];
}

export const LiliMultiSelect = (props: ILiliMultiSelectProps) => {
  const { maxSelectedLabels, items, onChange, defaultValue } = props;

  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const getItemIndex = (itemValue: string) => items.findIndex((i) => i.value === itemValue);

  const canSelectMoreItems = useMemo(
    () => !(maxSelectedLabels === selectedValues.length || maxSelectedLabels < selectedValues.length),
    [selectedValues],
  );

  useEffect(() => {
    onChange(selectedValues);
  }, [selectedValues]);

  useEffect(() => {
    if (defaultValue) {
      setSelectedValues(defaultValue);
    }
  }, []);

  const renderItemTemplate = (option: { title: string; value: string }) => (
    <div className='absolute bg-bw01 flex items-center border-b border-bw03 py-[8px] px-[8px] inset-0'>
      <div
        className={`${selectedValues.includes(option.value) ? 'bg-action00' : ''} w-full relative rounded-[6px] flex items-center group hover:bg-action00 px-[12px] py-[6px]`}
      >
        <Heading
          title={option.title}
          className={`${selectedValues.includes(option.value) ? 'text-action03' : 'text-bw08'} font-metricR group-hover:text-action03 text-[14px]`}
        />
      </div>
    </div>
  );

  const handleClearChip = (event: React.MouseEvent<HTMLButtonElement>, itemValue: string) => {
    event.stopPropagation();
    const updatedSelectedItems = selectedValues.filter((item) => item !== itemValue);
    setSelectedValues(updatedSelectedItems);
  };

  const activeSelectedItemTemplate = (itemValue: string) => (
    <span className='flex cursor-default w-fit bg-action00 items-center gap-[6px] py-[2px] px-[12px] rounded-[20px]'>
      <Heading title={items[getItemIndex(itemValue)].title} className='font-metricR text-[14px] text-bw08' />
      <button onClick={(event) => handleClearChip(event, itemValue)} className='w-fit' type='button'>
        <CloseIcon className='w-[16px] stroke-action03 text-action03' />
      </button>
    </span>
  );

  return (
    <div className='w-full h-[80px]' id='LiliMultiSelectWrapper'>
      <MultiSelect
        panelHeaderTemplate={<div className='hidden' />}
        selectedItemTemplate={(item) => (selectedValues.length > 0 ? activeSelectedItemTemplate(item) : null)}
        itemTemplate={(optionValue) => renderItemTemplate(optionValue)}
        itemClassName={`relative ${canSelectMoreItems ? '' : 'pointer-events-none'}`}
        panelClassName='border border-bw03 px-[2px] py-[2px] mt-[4px] shadow-bottomShadow'
        value={selectedValues}
        optionLabel='title'
        optionValue='value'
        onChange={(e) => setSelectedValues(e.value)}
        options={items}
        placeholder='Select Countries'
        maxSelectedLabels={3}
        className='outline-none shadow-none w-full border border-bw04 rounded-[8px] h-[54px] flex items-center'
      />
    </div>
  );
};
